/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { staffLocationType, type StaffRole, type UserStaff } from '../../types/user';
import { PLAIN_PLACE, type ManagedLocationType, type PlaceShape } from '../nav';
import { placePath as pathAt, sectionRoot, splitPlacePath } from '../place-path';

const STORAGE_KEY = 'gym_admin_selected_gym';

/**
 * The place kinds this panel manages. Trophies and their stages are in the same
 * switcher as gyms and crags, for the same reason crags are: same auth, same
 * roles, same shell — only the menu differs.
 */
const MANAGED_TYPES: ManagedLocationType[] = ['gym', 'crag', 'trophy', 'competition'];

interface GymAdminContextValue {
  /** Every place the user is confirmed staff of (the switchable set). */
  staffGyms: UserStaff[];
  /**
   * Slug of the place being managed — the first segment of the URL, when it
   * names one of `staffGyms`. Null otherwise: the guards then redirect, so
   * nothing under the layout renders with it null.
   */
  selectedGymSlug: string | null;
  /**
   * Switch the active place: navigates to the same section at the new slug.
   * The record being looked at does not carry over — it was the old place's.
   */
  selectGym: (slug: string) => void;
  /**
   * A path inside the panel at the active place: `placePath('/rounds')` is
   * `/junglerock/rounds`. Every link and redirect goes through it, because the
   * place is a URL segment and a bare `/rounds` is an old-style link.
   */
  placePath: (path: string) => string;
  /**
   * The path under the place — `/rounds/12` on `/junglerock/rounds/12`, '' at
   * the place's root. What the sidebar highlights and the header names.
   */
  sectionPath: string;
  /** The full staff record for the active gym. */
  currentGym: UserStaff | null;
  /** Which kind of place is selected — each has a different menu. */
  locationType: ManagedLocationType;
  /**
   * What the selected competition hangs off — a stage of a circuit, held at a
   * gym — which decides the few sections a kind alone cannot. Plain for
   * anything that is not a competition.
   */
  place: PlaceShape;
  /**
   * The place the active competition was reached through — a stage's trophy,
   * a hosted event's gym — when the user manages that too.
   */
  parentGym: UserStaff | null;
  /** Staff roles the user holds at the active gym. */
  roles: StaffRole[];
  /** True when the user holds at least one of the given roles at the active gym. */
  hasAnyRole: (roles: StaffRole[]) => boolean;
}

const GymAdminContext = createContext<GymAdminContextValue | undefined>(undefined);

/**
 * Where to send somebody who has not said which place: the one they managed
 * last, if they still can, else the first they can. Read by the guards for the
 * bare root and for links from before the place was in the URL.
 */
export function rememberedPlaceSlug(staffGyms: UserStaff[]): string | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && staffGyms.some((g) => g.gym_slug === stored)) return stored;
  // Never an archived season by default: it is out of the switcher, and
  // landing there would look like the panel had lost everything else. It is
  // still reachable by URL, which is how it gets taken back out (see
  // GymSwitcher).
  const current = staffGyms.filter((g) => g.location_status !== 'archived');
  return (current[0] ?? staffGyms[0])?.gym_slug ?? null;
}

export function GymAdminProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const staffGyms = useMemo(
    () =>
      (user?.staff ?? []).filter(
        (s) =>
          s.status === 'confirmed' &&
          MANAGED_TYPES.includes(staffLocationType(s) as ManagedLocationType)
      ),
    [user?.staff]
  );

  // The URL is the selection. It is not held in state on purpose: state would
  // be a second copy that has to be reconciled with the address bar on every
  // navigation, back button and reload, and the old localStorage copy was
  // exactly that — a link opened the place the reader last looked at, not the
  // one it named.
  const { slug: urlSlug, sectionPath } = useMemo(() => splitPlacePath(pathname), [pathname]);

  const selectedGymSlug = useMemo(
    () => (urlSlug && staffGyms.some((g) => g.gym_slug === urlSlug) ? urlSlug : null),
    [staffGyms, urlSlug]
  );

  // Remembered only to pick a place for a URL that names none.
  useEffect(() => {
    if (selectedGymSlug) {
      localStorage.setItem(STORAGE_KEY, selectedGymSlug);
    }
  }, [selectedGymSlug]);

  const selectGym = useCallback(
    (slug: string) => navigate(pathAt(slug, sectionRoot(sectionPath))),
    [navigate, sectionPath]
  );

  const placePath = useCallback(
    (path: string) => (selectedGymSlug ? pathAt(selectedGymSlug, path) : path),
    [selectedGymSlug]
  );

  const currentGym = useMemo(
    () => staffGyms.find((g) => g.gym_slug === selectedGymSlug) ?? null,
    [staffGyms, selectedGymSlug]
  );

  const roles = useMemo(() => currentGym?.staff_roles ?? [], [currentGym]);

  const locationType = useMemo(
    () => staffLocationType(currentGym) as ManagedLocationType,
    [currentGym]
  );

  const place = useMemo<PlaceShape>(
    () =>
      locationType === 'competition'
        ? { stage: !!currentGym?.trophy_slug, hostedAtVenue: !!currentGym?.venue_slug }
        : PLAIN_PLACE,
    [locationType, currentGym]
  );

  // The place a selected competition hangs under. Only ever set for a
  // competition, and only when the user can also manage that place — which is
  // the whole point of showing it: it is the way back up.
  const parentGym = useMemo(() => {
    if (!currentGym?.parent_slug) return null;
    return staffGyms.find((g) => g.gym_slug === currentGym.parent_slug) ?? null;
  }, [staffGyms, currentGym]);

  const hasAnyRole = useCallback(
    (required: StaffRole[]) => required.some((role) => roles.includes(role)),
    [roles]
  );

  const value = useMemo<GymAdminContextValue>(
    () => ({
      staffGyms,
      selectedGymSlug,
      selectGym,
      placePath,
      sectionPath,
      currentGym,
      locationType,
      place,
      parentGym,
      roles,
      hasAnyRole,
    }),
    [
      staffGyms,
      selectedGymSlug,
      selectGym,
      placePath,
      sectionPath,
      currentGym,
      locationType,
      place,
      parentGym,
      roles,
      hasAnyRole,
    ]
  );

  return <GymAdminContext.Provider value={value}>{children}</GymAdminContext.Provider>;
}

export function useGymAdmin(): GymAdminContextValue {
  const context = useContext(GymAdminContext);
  if (context === undefined) {
    throw new Error('useGymAdmin must be used within a GymAdminProvider');
  }
  return context;
}
